"use client";

import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Heading } from "../base/heading";
import { HorizontalSpacer } from "../layout/horizontal-spacer";
import { Text } from "./text";
import "@splidejs/react-splide/css";
import { getMediaQuery } from "@/contexts/media-context";
import { lockBodyWhenModalIsOpen } from "@/utils/modal-utils";
import { classNames } from "@/utils/styling-utils";
import { Dialog } from "../base/dialog";
import { ArrowRightIcon } from "../icons/icons";

interface SwiperProps {
  fullWidth?: boolean;
  heading?: string;
  isSidebar?: boolean;
  items: ReactNode[];
  itemsPerPageMedium?: number /* itemsPerPageMedium gjelder for medium skjermstørrelse (600px-768px) */;
  itemsPerPageLarge?: number /* itemsPerPageLarge gjelder for large skjermstørrelse (768px og større) */;
  itemsPerPageXLarge?: number /* itemsPerPageXLarge gjelder for large skjermstørrelse (1024px og større) */;
  notInfinite?: boolean;
  showArrowsAlways?: boolean;
  showArrowsOnTop?: boolean;
  text?: string;
  isImageGallery?: boolean;
}

export const Swiper = ({
  fullWidth,
  heading,
  isSidebar,
  isImageGallery = false,
  items,
  itemsPerPageMedium,
  itemsPerPageLarge,
  itemsPerPageXLarge,
  notInfinite,
  showArrowsAlways,
  showArrowsOnTop,
  text,
}: SwiperProps) => {
  // Definerer standard antall elementer per skjermstørrelse
  const defaultNumberOfItems = 3;
  const itemsPerPageSmallScreen = 1;
  const itemsPerPageMediumScreen = itemsPerPageMedium ?? 2;
  const itemsPerPageLargeScreen = isSidebar ? 1 : (itemsPerPageLarge ?? defaultNumberOfItems); // sidebar blir først synlig på stor skjerm
  const itemsPerPageXLargeScreen = isSidebar ? 1 : (itemsPerPageXLarge ?? defaultNumberOfItems);

  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ISSUE FIX: Store references to Splide instance and container element
  // This allows us to manage initialization and refresh correctly
  const splideRef = useRef<{ refresh: () => void } | null>(null);
  const swiperContainerRef = useRef<HTMLDivElement>(null);

  // ISSUE FIX: Capture the Splide instance when it's mounted
  // This gives us access to refresh and other methods later
  const handleMounted = (splide: { refresh: () => void }) => {
    splideRef.current = splide;

    // ISSUE FIX: Initial refresh with delay to ensure proper initialization
    // The delay allows time for images to start loading and layout to stabilize
    setTimeout(() => {
      if (splideRef.current) {
        splideRef.current.refresh();
      }
    }, 300);
  };

  // ISSUE FIX: Use Intersection Observer to detect when the slider becomes visible
  // This ensures we only initialize the slider when it's in the viewport
  // Prevents incorrect dimension calculations when the slider is far down the page
  useEffect(() => {
    if (!swiperContainerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // ISSUE FIX: Refresh the slider when it becomes visible
          // This recalculates dimensions when the component is actually in the viewport
          setTimeout(() => {
            if (splideRef.current) {
              splideRef.current.refresh();
            }
          }, 100);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(swiperContainerRef.current);

    return () => observer.disconnect();
  }, []);

  // ISSUE FIX: Listen for image load events and window resize
  // Slider dimensions can be affected by image loading and window resizing
  // This ensures the slider recalculates its dimensions after these events
  useEffect(() => {
    // ISSUE FIX: Refresh slider when images load
    // Images often change the layout dimensions once they're loaded
    const handleImageLoad = () => {
      if (splideRef.current) {
        splideRef.current.refresh();
      }
    };

    // Find all images within the swiper
    const images = swiperContainerRef.current?.querySelectorAll("img");

    if (images && images.length > 0) {
      for (const img of Array.from(images)) {
        // For already loaded images
        if (img.complete) {
          handleImageLoad();
        } else {
          img.addEventListener("load", handleImageLoad);
        }
      }
    }

    // ISSUE FIX: Handle window resize events
    // Window resizing can affect the slider's layout calculations
    const handleResize = () => {
      if (splideRef.current) {
        splideRef.current.refresh();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (images && images.length > 0) {
        for (const img of Array.from(images)) {
          img.removeEventListener("load", handleImageLoad);
        }
      }
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Åpner modal ved klikk på slide dersom skjermvidden er større enn md
  // og swiperen er ImageGallery
  const handleSlideClick = (index: number) => {
    if (window.matchMedia(getMediaQuery("md")).matches && isImageGallery) {
      setSelectedSlide(index);
      setModalOpen(true);
      lockBodyWhenModalIsOpen(true);
    }
  };

  //lukker modalen med escape knappen
  useEffect(() => {
    const handleKeyDown = (event: WindowEventMap["keydown"]) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setModalOpen(false);
        lockBodyWhenModalIsOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener("keydown", handleKeyDown as EventListener);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown as EventListener);
    };
  }, [modalOpen]);

  //lukker modal dersom man klikker utenfor bilde eller knapp
  const handleOutsideClick = (event: MouseEvent) => {
    const targetElement = event.target as HTMLElement;
    const closestImageOrButton = targetElement.closest("img, button");

    if (!closestImageOrButton) {
      lockBodyWhenModalIsOpen(false);
      setModalOpen(false);
    }
  };

  function useOutsideClick(callback: (event: MouseEvent) => void) {
    useEffect(() => {
      //sjekker om det blir klikket
      const handleClick = (event: MouseEvent) => {
        callback(event);
      };

      document.addEventListener("mousedown", handleClick);

      return () => {
        document.removeEventListener("mousedown", handleClick);
      };
    }, [callback]);

    return null;
  }

  //sjekker om det blir klikket
  const refModal = useOutsideClick((event: MouseEvent) => {
    //dersom modalen er åpen og det blir klikket utenfor bilde eller knapp vil modalen lukkes
    if (modalOpen) {
      handleOutsideClick(event);
    }
  });
  return (
    // ISSUE FIX: Added container ref to track component visibility
    <div ref={swiperContainerRef}>
      {heading && <Heading type="h2">{heading}</Heading>}
      {text && (
        <>
          <Text value={text} size="large" />
          <HorizontalSpacer size="medium" />
        </>
      )}
      <Splide
        // ISSUE FIX: Added onMounted callback to capture Splide instance
        onMounted={handleMounted}
        aria-label="Slider"
        className={classNames("swiper", fullWidth && "swiper--full-width", isSidebar && "swiper--sidebar")}
        hasTrack={false}
        options={{
          arrows: showArrowsAlways ? showArrowsAlways : isSidebar ? false : items.length > defaultNumberOfItems,
          breakpoints: {
            // Breakpoints her må match dem i variables.module.scss > NB: media-query her er max-width, og ikke min-width, som vi har ellers
            // Her må dem ligge i rekkefølge størst til minst
            1280: {
              arrows: showArrowsAlways ? showArrowsAlways : false,
              pagination: isSidebar ? items.length > 1 : items.length > defaultNumberOfItems,
              perPage: itemsPerPageXLargeScreen,
            },
            1024: {
              arrows: showArrowsAlways ? showArrowsAlways : false,
              pagination: isSidebar ? items.length > 1 : items.length > defaultNumberOfItems,
              perPage: itemsPerPageLargeScreen,
            },
            768: {
              arrows: showArrowsAlways ? showArrowsAlways : false,
              pagination: items.length > 2,
              perPage: itemsPerPageMediumScreen,
            },
            600: {
              arrows: showArrowsAlways ? showArrowsAlways : false,
              pagination: items.length > 1,
              perPage: itemsPerPageSmallScreen,
            },
          },
          easing: "linear",
          gap: "2.4rem", // tilsvarer $left-and-right-inset
          lazyLoad: "nearby",
          pagination: !!isSidebar,
          paginationKeyboard: true,
          perPage: itemsPerPageXLargeScreen,
          role: "",
          type: notInfinite || isImageGallery ? "slide" : "loop",
          width: "100%",
          flickMaxPages: 1,
          perMove: 1,
        }}
        role="slider"
        tag="section"
      >
        <div className="splide__arrows">
          <button
            type="button"
            aria-label="Scroll til venstre"
            className={classNames(
              "splide__arrow splide__arrow--prev swiper__arrow-button swiper__arrow-button--prev",
              showArrowsOnTop && "swiper__arrow-button--on-top",
            )}
          >
            <ArrowRightIcon size="large" />
          </button>
          <button
            type="button"
            aria-label="Scroll til høyre"
            className={classNames(
              "splide__arrow splide__arrow--next swiper__arrow-button swiper__arrow-button--next",
              showArrowsOnTop && "swiper__arrow-button--on-top",
            )}
          >
            <ArrowRightIcon size="large" />
          </button>
        </div>
        <SplideTrack>
          {items.map((item, index) => (
            <SplideSlide key={index} onClick={() => handleSlideClick(index)}>
              {item}
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>
      <Dialog isOpen={modalOpen} id={"swiper-dialog"} placement="center" width="auto">
        <div className="modal" ref={refModal}>
          {selectedSlide !== null && (
            <Splide
              aria-label="Image Gallery"
              className="modal__swiper"
              hasTrack={false}
              options={{
                type: "loop",
                width: "100%",
                perPage: 1,
                perMove: 1,
                arrows: true,
                paginationKeyboard: true,
                speed: 1000,
                easing: "ease",
                keyboard: "global",
                start: selectedSlide,
              }}
            >
              <SplideTrack>
                {items.map((item, index) => (
                  <SplideSlide key={index}>{item}</SplideSlide>
                ))}
              </SplideTrack>
              <div className="splide__arrows">
                <button
                  type="button"
                  aria-label="Scroll til venstre"
                  className="splide__arrow splide__arrow--prev swiper__arrow-button swiper__arrow-button--prev"
                >
                  <ArrowRightIcon size="large" />
                </button>
                <button
                  type="button"
                  aria-label="Scroll til høyre"
                  className="splide__arrow splide__arrow--next swiper__arrow-button swiper__arrow-button--next"
                >
                  <ArrowRightIcon size="large" />
                </button>
              </div>
            </Splide>
          )}
        </div>
      </Dialog>

      {!isSidebar && <HorizontalSpacer size="large" />}
    </div>
  );
};
